import React, { useEffect, useState } from "react";
import {
  Box,
  TextField,
  Button,
  InputLabel,
  FormControl,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { createProject, editProject, getMembers } from "../api/project";
import { useProject } from "../hooks/project";
import { useUser } from "../hooks/user";
import { createGuid, encrypt } from "../utils/cryptoHelper";

const EditProjectView = () => {
  const navigate = useNavigate();
  const user = useUser();
  // grab the module name from the URL parameters where is in Add Project button ProjectsView Component. 
  const { moduleName } = useParams();
  const firstTimeCreate = moduleName === "create-project";
  // localStorage may still store previously viewed project data; ignore it when creating
  // so if the first time will be undefined which allows creating a new project 
  // otherwise, use the existing project data from the localStorage
  const storedProject = useProject();
  const project = firstTimeCreate ? undefined : storedProject;

  const [canEditProject, setCanEditProject] = useState(firstTimeCreate);
  const [projectName, setProjectName] = useState(project?.PROJ_NAME || "");
  const [jobNumber, setJobNumber] = useState(project?.job_number || "");
  const [clientName, setClientName] = useState(project?.PROJ_CLNT || "");
  const [contractNumber, setContractNumber] = useState(project?.PROJ_CONT || "");
  const [taskOrderNumber, setTaskOrderNumber] = useState(project?.task_number || "");
  const [projectStatus, setProjectStatus] = useState(project?.PROJ_STAT || "Draft");
  const [saveSuccessfulDialogOpen, setSaveSuccessfulDialogOpen] = useState(false);
  const [errors, setErrors] = useState({
    projectName: false,
    jobNumber: false,
    clientName: false,
    contractNumber: false,
    taskOrderNumber: false,
  });

  useEffect(() => {
    if (firstTimeCreate) return;

    getMembers(project.project_id, (data) => {
      const currentMember = data?.find(
        (member) => String(member.user_id).toLowerCase() === String(user.id).toLowerCase()
      );
      setCanEditProject(
        currentMember?.role === "Project Admin" || currentMember?.role === "Geologist"
      );
    });
    // if any three of these conditions is true then rerun the effect to check the user's role in the project
  }, [firstTimeCreate, project?.project_id, user?.id]);

  const isAllFieldsEmpty = () =>
    projectName.trim() === "" &&
    jobNumber.trim() === "" &&
    clientName.trim() === "" &&
    contractNumber.trim() === "" &&
    taskOrderNumber.trim() === "";

  const validateFields = () => {
    // don't show per-field errors on a completely untouched form
    if (isAllFieldsEmpty()) {
      setErrors({
        projectName: false,
        jobNumber: false,
        clientName: false,
        contractNumber: false,
        taskOrderNumber: false,
      });
      return false;
    }

    const newErrors = {
      projectName: projectName.trim() === "",
      jobNumber: jobNumber.trim() === "",
      clientName: clientName.trim() === "",
      contractNumber: contractNumber.trim() === "",
      taskOrderNumber: taskOrderNumber.trim() === "",
    };

    setErrors(newErrors);
    return !Object.values(newErrors).some((error) => error);
  };

  const handleSubmit = () => {
    if (firstTimeCreate) {
      if (!validateFields()) return;

      const newProject = {
        PROJ_ID: createGuid(),
        PROJ_NAME: projectName,
        PROJ_STAT: "Draft",
        job_number: jobNumber,
        PROJ_CLNT: clientName,
        PROJ_CONT: contractNumber,
        task_number: taskOrderNumber,
      };
      new Promise((resolve, reject) => {
        createProject(newProject, resolve, reject);
      })
        .then(() => {
          // DataView reads the current project from localStorage, not from the URL query
          window.localStorage.setItem(
            "project",
            encrypt(JSON.stringify({ ...newProject, project_id: newProject.PROJ_ID }))
          );
          navigate(`/modules/data?id=${newProject.PROJ_ID}`);
        })
        .catch((error) => {
          console.error("Failed to create project:", error);
        });
      return;
    }

    const allEmpty = isAllFieldsEmpty();

    // editing: fall back to the previous value for any field left blank, never block with errors
    const finalProjectName = projectName.trim() === "" ? project.PROJ_NAME || "" : projectName;
    const finalJobNumber = jobNumber.trim() === "" ? project.job_number || "" : jobNumber;
    const finalClientName = clientName.trim() === "" ? project.PROJ_CLNT || "" : clientName;
    const finalContractNumber = contractNumber.trim() === "" ? project.PROJ_CONT || "" : contractNumber;
    const finalTaskOrderNumber = taskOrderNumber.trim() === "" ? project.task_number || "" : taskOrderNumber;

    if (allEmpty) {
      setProjectStatus(project.PROJ_STAT || "Draft");
    }
    setProjectName(finalProjectName);
    setJobNumber(finalJobNumber);
    setClientName(finalClientName);
    setContractNumber(finalContractNumber);
    setTaskOrderNumber(finalTaskOrderNumber);

    const updatedProject = {
      ...project,
      PROJ_NAME: finalProjectName,
      PROJ_CLNT: finalClientName,
      PROJ_STAT: allEmpty ? project.PROJ_STAT || "Draft" : projectStatus,
      job_number: finalJobNumber,
      PROJ_CONT: finalContractNumber,
      task_number: finalTaskOrderNumber,
      modified_at: new Date().toISOString(),
    };

    editProject(
      updatedProject,
      () => {
        // Backend Update Successfully and return here   
        window.localStorage.setItem("project", encrypt(JSON.stringify(updatedProject)));
        setSaveSuccessfulDialogOpen(true);
      },
      (error) => {
        console.error("Failed to update project:", error);
      }
    );
  };

  const containerStyle = {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    width: "100%",
    height: "100vh",
    padding: "100px 16px",
  };

  const formStyle = {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    width: "100%",
    maxWidth: "1000px",
    marginBottom: "10px",
  };

  return (
    <Box sx={containerStyle}>
      <Box sx={formStyle}>
          <TextField
            label="*Project name"
            value={projectName}
            onChange={(event) => setProjectName(event.target.value)}
            onBlur = {(event) => setProjectName(event.target.value.replace(/[^a-zA-Z0-9]/g, '').trim())}
            variant="standard"
            error={errors.projectName}
            helperText={errors.projectName ? "Required fields must be filled in" : ""}
            sx={{ m: 1, width: "70%" }}
            disabled={!canEditProject}
          />

          <TextField
            label="*Job Number"
            value={jobNumber}
            onChange={(event) => setJobNumber(event.target.value)}
            onBlur = {(event) => setJobNumber(event.target.value.replace(/[^a-zA-Z0-9]/g, '').trim())}
            variant="standard"
            error={errors.jobNumber}
            helperText={errors.jobNumber ? "Required fields must be filled in" : ""}
            sx={{ m: 1, width: "70%" }}
            disabled={!canEditProject}
          />
        
          <TextField
            label="*Client Name"
            value={clientName}
            onChange={(event) => setClientName(event.target.value)}
            onBlur = {(event) => setClientName(event.target.value.replace(/[^a-zA-Z0-9]/g, '').trim())}
            variant="standard"
            error={errors.clientName}
            helperText={errors.clientName ? "Required fields must be filled in" : ""}
            sx={{ m: 1, width: "70%" }}
            disabled={!canEditProject}
          />

          <TextField
            label="*Contract Number"
            value={contractNumber}
            onChange={(event) => setContractNumber(event.target.value)}
            onBlur = {(event) => setContractNumber(event.target.value.replace(/[^a-zA-Z0-9]/g, '').trim())}
            variant="standard"
            error={errors.contractNumber}
            helperText={errors.contractNumber ? "Required fields must be filled in" : ""}
            sx={{ m: 1, width: "70%" }}
            disabled={!canEditProject}
          />

          <TextField
            label="*Task Order Number"
            value={taskOrderNumber}
            onChange={(event) => setTaskOrderNumber(event.target.value)}
            onBlur = {(event) => setTaskOrderNumber(event.target.value.replace(/[^a-zA-Z0-9]/g, '').trim())}
            variant="standard"
            error={errors.taskOrderNumber}
            helperText={errors.taskOrderNumber ? "Required fields must be filled in" : ""}
            sx={{ m: 1, width: "70%" }}
            disabled={!canEditProject}
          />

          <FormControl variant="standard" sx={{ m: 1, width: "70%" }}>
            <InputLabel>*Project Status</InputLabel>
            {firstTimeCreate ? (
              <Select label="*Project Status" value="Draft" disabled>
                <MenuItem value="Draft">Draft</MenuItem>
              </Select>
            ) : (
              <Select
                label="*Project Status"
                value={projectStatus}
                onChange={(event) => setProjectStatus(event.target.value)}
                disabled={!canEditProject}
              >
                <MenuItem value="Draft">Draft</MenuItem>
                <MenuItem value="Preliminary">Preliminary</MenuItem>
                <MenuItem value="Final">Final</MenuItem>
              </Select>
            )}
          </FormControl>

        {canEditProject && (
          <Button
            variant="contained"
            onClick={() => handleSubmit()}
            sx={{ m: 1, width: "50%" }}
          >
            {firstTimeCreate ? "Submit" : "Save"}
          </Button>
        )}
      </Box>
      <Dialog
        open={saveSuccessfulDialogOpen}
        onClose={() => setSaveSuccessfulDialogOpen(false)}
      >
        <DialogTitle>Save successful</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Project information has been saved successfully.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setSaveSuccessfulDialogOpen(false);
            }}
            autoFocus
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EditProjectView;
